import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FaBell,
    FaEnvelope,
    FaShoppingBag,
    FaCheckCircle,
    FaExclamationTriangle,
    FaSlidersH
} from 'react-icons/fa';
import LuxuryButton from '../../components/ui/LuxuryButton';
import { UserService } from '../../api/user.api';
import {designTokens} from "../../theme/designTokens";

const NotificationsModal = ({ isOpen, onClose, user }) => {
    const [preferences, setPreferences] = useState({
        email: {
            marketing: true,
            orderUpdates: true,
            shippingNotifications: true,
            promotions: false,
            weeklyDigest: true,
            productLaunches: true
        },
        push: {
            orderUpdates: true,
            shippingNotifications: true,
            promotions: false,
            specialOffers: false,
            cartReminders: false,
            wishlistUpdates: true
        },
        sms: {
            shippingUpdates: false,
            orderConfirmation: false,
            securityAlerts: true
        },
        newsletter: true,
        personalizedRecommendations: true,
        quietHours: {
            enabled: false,
            start: '22:00',
            end: '08:00'
        }
    });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        if (isOpen && user) {
            loadPreferences();
        }
    }, [isOpen, user]);

    const loadPreferences = async () => {
        setLoading(true);
        try {
            // Try to fetch user preferences from API
            // For now, we'll use localStorage as a fallback
            const savedPreferences = localStorage.getItem('kromaic_notification_prefs');
            if (savedPreferences) {
                setPreferences(JSON.parse(savedPreferences));
            }
        } catch (err) {
            console.error('Error loading preferences:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = (category, key) => {
        setPreferences(prev => ({
            ...prev,
            [category]: {
                ...prev[category],
                [key]: !prev[category][key]
            }
        }));
    };

    const handleGlobalToggle = (key) => {
        setPreferences(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const handleQuietHoursToggle = () => {
        setPreferences(prev => ({
            ...prev,
            quietHours: {
                ...prev.quietHours,
                enabled: !prev.quietHours.enabled
            }
        }));
    };

    const handleQuietHoursChange = (field, value) => {
        setPreferences(prev => ({
            ...prev,
            quietHours: {
                ...prev.quietHours,
                [field]: value
            }
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        setSuccess(null);

        try {
            // Save preferences to API
            await UserService.updatePreferences({
                notifications: preferences
            });

            // Also save to localStorage as backup
            localStorage.setItem('kromaic_notification_prefs', JSON.stringify(preferences));

            // Update newsletter subscription if changed
            if (preferences.newsletter) {
                await UserService.toggleNewsletter();
            }

            setSuccess('Notification preferences saved successfully!');

            // Clear success message after 3 seconds
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError(err.message || 'Failed to save preferences');
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        setPreferences({
            email: {
                marketing: true,
                orderUpdates: true,
                shippingNotifications: true,
                promotions: false,
                weeklyDigest: true,
                productLaunches: true
            },
            push: {
                orderUpdates: true,
                shippingNotifications: true,
                promotions: false,
                specialOffers: false,
                cartReminders: false,
                wishlistUpdates: true
            },
            sms: {
                shippingUpdates: false,
                orderConfirmation: false,
                securityAlerts: true
            },
            newsletter: true,
            personalizedRecommendations: true,
            quietHours: {
                enabled: false,
                start: '22:00',
                end: '08:00'
            }
        });
    };

    if (!isOpen) return null;

    const NotificationSection = ({ title, icon: Icon, category, items }) => (
        <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-champagne/10 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-champagne" />
                </div>
                <div className="flex-1">
                    <h3 className="font-medium text-charcoal">{title}</h3>
                    <p className="text-sm text-charcoal/60">Control your {title.toLowerCase()} notifications</p>
                </div>
            </div>

            <div className="space-y-3">
                {Object.entries(items).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between p-3 rounded-lg hover:bg-nude/5 transition-colors">
                        <div>
                            <h4 className="text-sm font-medium text-charcoal">{formatKey(key)}</h4>
                            <p className="text-xs text-charcoal/50 mt-1">
                                {getNotificationDescription(key)}
                            </p>
                        </div>
                        <button
                            onClick={() => handleToggle(category, key)}
                            className="relative focus:outline-none"
                            disabled={loading}
                        >
                            <div className="w-12 h-6 rounded-full transition-colors duration-200 ease-in-out"
                                 style={{
                                     backgroundColor: value ? '#C8B568' : '#E8E4DC',
                                     opacity: loading ? 0.5 : 1
                                 }}>
                                <motion.div
                                    className="w-5 h-5 rounded-full bg-white shadow-md"
                                    initial={false}
                                    animate={{
                                        x: value ? 26 : 2,
                                        y: 2
                                    }}
                                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                                />
                            </div>
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );

    const formatKey = (key) => {
        return key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
    };

    const getNotificationDescription = (key) => {
        const descriptions = {
            marketing: 'Updates about new products and services',
            orderUpdates: 'Order confirmations and status changes',
            shippingNotifications: 'Shipping updates and delivery alerts',
            promotions: 'Special promotions and discount offers',
            weeklyDigest: 'Weekly summary of activities and news',
            productLaunches: 'New product announcements',
            specialOffers: 'Exclusive offers and early access',
            cartReminders: 'Reminders about items in your cart',
            wishlistUpdates: 'Updates about wishlist items',
            shippingUpdates: 'Real-time shipping notifications',
            orderConfirmation: 'Order confirmation messages',
            securityAlerts: 'Important security notifications'
        };
        return descriptions[key] || 'Notification updates';
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto"
                onClick={onClose}
            >
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ type: "spring", damping: 25, stiffness: 300 }}
                    className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-8 overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="p-6 border-b border-nude/30">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-champagne/20 to-sage/20 flex items-center justify-center">
                                    <FaBell className="w-5 h-5 text-champagne" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-medium text-charcoal">Notification Preferences</h2>
                                    <p className="text-sm text-charcoal/60">Customize how we communicate with you</p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="text-charcoal/50 hover:text-charcoal transition-colors p-2 hover:bg-nude/10 rounded-full"
                            >
                                ✕
                            </button>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="p-6 max-h-[70vh] overflow-y-auto">
                        {loading ? (
                            <div className="text-center py-8">
                                <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-champagne border-t-transparent"></div>
                                <p className="mt-2 text-sm text-charcoal/60">Loading preferences...</p>
                            </div>
                        ) : (
                            <div className="space-y-8">
                                {/* Notifications */}
                                <AnimatePresence>
                                    {error && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            className="p-4 bg-error/10 border border-error/20 rounded-xl flex items-start gap-3"
                                        >
                                            <FaExclamationTriangle className="w-5 h-5 text-error flex-shrink-0 mt-0.5" />
                                            <p className="text-sm text-error flex-1">{error}</p>
                                        </motion.div>
                                    )}

                                    {success && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            className="p-4 bg-sage/10 border border-sage/20 rounded-xl flex items-start gap-3"
                                        >
                                            <FaCheckCircle className="w-5 h-5 text-sage flex-shrink-0 mt-0.5" />
                                            <p className="text-sm text-sage flex-1">{success}</p>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {/* Global Preferences */}
                                <div className="bg-gradient-to-r from-champagne/5 to-sage/5 rounded-xl p-6">
                                    <h3 className="font-medium text-charcoal mb-4">Global Preferences</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="flex items-center justify-between p-3 rounded-lg bg-white/50">
                                            <div>
                                                <h4 className="text-sm font-medium text-charcoal">Newsletter Subscription</h4>
                                                <p className="text-xs text-charcoal/50">Weekly beauty tips and updates</p>
                                            </div>
                                            <button
                                                onClick={() => handleGlobalToggle('newsletter')}
                                                className="relative focus:outline-none"
                                            >
                                                <div className="w-12 h-6 rounded-full transition-colors duration-200 ease-in-out"
                                                     style={{
                                                         backgroundColor: preferences.newsletter ? '#C8B568' : '#E8E4DC'
                                                     }}>
                                                    <motion.div
                                                        className="w-5 h-5 rounded-full bg-white shadow-md"
                                                        initial={false}
                                                        animate={{
                                                            x: preferences.newsletter ? 26 : 2,
                                                            y: 2
                                                        }}
                                                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                                                    />
                                                </div>
                                            </button>
                                        </div>

                                        <div className="flex items-center justify-between p-3 rounded-lg bg-white/50">
                                            <div>
                                                <h4 className="text-sm font-medium text-charcoal">Personalized Recommendations</h4>
                                                <p className="text-xs text-charcoal/50">Product suggestions based on your preferences</p>
                                            </div>
                                            <button
                                                onClick={() => handleGlobalToggle('personalizedRecommendations')}
                                                className="relative focus:outline-none"
                                            >
                                                <div className="w-12 h-6 rounded-full transition-colors duration-200 ease-in-out"
                                                     style={{
                                                         backgroundColor: preferences.personalizedRecommendations ? '#C8B568' : '#E8E4DC'
                                                     }}>
                                                    <motion.div
                                                        className="w-5 h-5 rounded-full bg-white shadow-md"
                                                        initial={false}
                                                        animate={{
                                                            x: preferences.personalizedRecommendations ? 26 : 2,
                                                            y: 2
                                                        }}
                                                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                                                    />
                                                </div>
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Email Notifications */}
                                <NotificationSection
                                    title="Email Notifications"
                                    icon={FaEnvelope}
                                    category="email"
                                    items={preferences.email}
                                />

                                {/* Push Notifications */}
                                <NotificationSection
                                    title="Push Notifications"
                                    icon={FaBell}
                                    category="push"
                                    items={preferences.push}
                                />

                                {/* SMS Notifications */}
                                <NotificationSection
                                    title="SMS Notifications"
                                    icon={FaShoppingBag}
                                    category="sms"
                                    items={preferences.sms}
                                />

                                {/* Quiet Hours */}
                                <div className="space-y-4">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-10 h-10 rounded-full bg-nude/20 flex items-center justify-center">
                                            <FaSlidersH className="w-5 h-5 text-charcoal/70" />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="font-medium text-charcoal">Quiet Hours</h3>
                                            <p className="text-sm text-charcoal/60">Pause notifications during specific hours</p>
                                        </div>
                                        <button
                                            onClick={handleQuietHoursToggle}
                                            className="relative focus:outline-none"
                                        >
                                            <div className="w-12 h-6 rounded-full transition-colors duration-200 ease-in-out"
                                                 style={{
                                                     backgroundColor: preferences.quietHours.enabled ? '#C8B568' : '#E8E4DC'
                                                 }}>
                                                <motion.div
                                                    className="w-5 h-5 rounded-full bg-white shadow-md"
                                                    initial={false}
                                                    animate={{
                                                        x: preferences.quietHours.enabled ? 26 : 2,
                                                        y: 2
                                                    }}
                                                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                                                />
                                            </div>
                                        </button>
                                    </div>

                                    {preferences.quietHours.enabled && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            className="grid grid-cols-2 gap-4 p-4 bg-nude/5 rounded-xl"
                                        >
                                            <div>
                                                <label className="block text-sm font-medium text-charcoal/80 mb-2">
                                                    Start Time
                                                </label>
                                                <input
                                                    type="time"
                                                    value={preferences.quietHours.start}
                                                    onChange={(e) => handleQuietHoursChange('start', e.target.value)}
                                                    className="w-full px-3 py-2 rounded-lg border border-nude/50 focus:border-champagne focus:outline-none"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-charcoal/80 mb-2">
                                                    End Time
                                                </label>
                                                <input
                                                    type="time"
                                                    value={preferences.quietHours.end}
                                                    onChange={(e) => handleQuietHoursChange('end', e.target.value)}
                                                    className="w-full px-3 py-2 rounded-lg border border-nude/50 focus:border-champagne focus:outline-none"
                                                />
                                            </div>
                                            <p className="text-xs text-charcoal/50 col-span-2">
                                                Notifications will be paused between {preferences.quietHours.start} and {preferences.quietHours.end}
                                            </p>
                                        </motion.div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="p-6 border-t border-nude/30 bg-white">
                        <div className="flex flex-col sm:flex-row gap-3">
                            <LuxuryButton
                                variant="outline"
                                size="medium"
                                onClick={handleReset}
                                disabled={loading || saving}
                                className="sm:flex-1"
                            >
                                Reset to Defaults
                            </LuxuryButton>
                            <LuxuryButton
                                variant="primary"
                                size="medium"
                                onClick={handleSave}
                                loading={saving}
                                disabled={loading || saving}
                                style={{
                                    background: designTokens.colors.gradients.champagne,
                                }}
                            >
                                Save Preferences
                            </LuxuryButton>
                        </div>
                        <p className="text-xs text-charcoal/50 text-center mt-4">
                            Changes may take a few minutes to take effect across all devices.
                        </p>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default NotificationsModal;